import React, { useCallback, useEffect, useRef } from 'react';
import { Box } from '@mui/material';
import { tokens } from '../../theme';

/**
 * Column header with a drag handle so MUI DataGrid v6 (community)
 * can still resize columns without DataGridPro / v7+.
 */
export function ResizableGridHeader({ label, field, width, onWidthChange, minWidth = 80 }) {
  const startX = useRef(0);
  const startWidth = useRef(width);

  const onMouseDown = useCallback((event) => {
    event.preventDefault();
    event.stopPropagation();
    startX.current = event.clientX;
    startWidth.current = width;

    const onMove = (e) => {
      const next = Math.max(minWidth, startWidth.current + (e.clientX - startX.current));
      onWidthChange(field, next);
    };
    const onUp = () => {
      window.removeEventListener('mousemove', onMove);
      window.removeEventListener('mouseup', onUp);
      document.body.style.cursor = '';
      document.body.style.userSelect = '';
    };

    document.body.style.cursor = 'col-resize';
    document.body.style.userSelect = 'none';
    window.addEventListener('mousemove', onMove);
    window.addEventListener('mouseup', onUp);
  }, [field, minWidth, onWidthChange, width]);

  return (
    <Box
      sx={{
        display: 'flex',
        alignItems: 'center',
        width: '100%',
        height: '100%',
        position: 'relative',
        pr: 1.25,
        boxSizing: 'border-box',
      }}
    >
      <Box
        component="span"
        sx={{
          fontWeight: 650,
          fontSize: 13,
          color: tokens.ink,
          overflow: 'hidden',
          textOverflow: 'ellipsis',
          whiteSpace: 'nowrap',
        }}
      >
        {label}
      </Box>
      <Box
        role="separator"
        aria-orientation="vertical"
        aria-label={`Resize ${label} column`}
        onMouseDown={onMouseDown}
        onClick={(e) => e.stopPropagation()}
        sx={{
          position: 'absolute',
          right: -4,
          top: -8,
          bottom: -8,
          width: 10,
          cursor: 'col-resize',
          zIndex: 2,
        }}
      />
    </Box>
  );
}

/** Apply current widths + resizable headers onto column defs. */
export function withResizableColumns(columns, widths, onWidthChange) {
  return columns.map((col) => {
    if (col.resizable === false) return col;
    const width = widths[col.field] ?? col.width ?? col.minWidth ?? 120;
    return {
      ...col,
      width,
      flex: undefined,
      minWidth: col.minWidth ?? 80,
      maxWidth: undefined,
      renderHeader: () => (
        <ResizableGridHeader
          label={col.headerName || col.field}
          field={col.field}
          width={width}
          minWidth={col.minWidth ?? 80}
          onWidthChange={onWidthChange}
        />
      ),
    };
  });
}

export function useColumnWidths(initialWidths) {
  const [widths, setWidths] = React.useState(initialWidths);
  const onWidthChange = useCallback((field, next) => {
    setWidths((prev) => (prev[field] === next ? prev : { ...prev, [field]: next }));
  }, []);

  useEffect(() => {
    setWidths((prev) => {
      let changed = false;
      const next = { ...prev };
      for (const [key, value] of Object.entries(initialWidths)) {
        if (next[key] == null) {
          next[key] = value;
          changed = true;
        }
      }
      return changed ? next : prev;
    });
  }, [initialWidths]);

  return { widths, onWidthChange, setWidths };
}
